/**
 * Catalog Manager
 *
 * Single page to manage the ten name-only catalogs: create, rename (renaming
 * into an existing name merges both entries after confirmation) and delete
 * (unassigns every reference). Mirrors the legacy Settings dialogs.
 */

import React, { useMemo, useState } from 'react';
import {
  Title,
  useCreate,
  useDelete,
  useGetList,
  useNotify,
  useTranslate,
  useUpdate,
} from 'react-admin';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as RenameIcon,
} from '@mui/icons-material';

const CATALOGS = [
  'tags',
  'interests',
  'job-titles',
  'companies',
  'contact-types',
  'lead-sources',
  'hotel-categories',
  'pms-systems',
  'lock-systems',
  'payment-processors',
] as const;

interface CatalogRow {
  id: number;
  name: string;
  usageCount: number;
}

export const CatalogManager = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [resource, setResource] = useState<string>(CATALOGS[0]);
  const [search, setSearch] = useState('');
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');

  const { data, isLoading, refetch } = useGetList<CatalogRow>(resource, {
    pagination: { page: 1, perPage: 5000 },
    sort: { field: 'name', order: 'ASC' },
  });
  const [create] = useCreate();
  const [update] = useUpdate();
  const [deleteOne] = useDelete();

  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`catalogs.${key}`, options);

  const rows = useMemo(() => {
    const all = data ?? [];
    if (!search.trim()) return all;
    const needle = search.trim().toLowerCase();
    return all.filter((row) => row.name.toLowerCase().includes(needle));
  }, [data, search]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await create(resource, { data: { name } }, { returnPromise: true });
      setNewName('');
      notify(t('created'), { type: 'info' });
      refetch();
    } catch {
      notify(t('error'), { type: 'error' });
    }
  };

  const handleRename = async (row: CatalogRow) => {
    const name = editingName.trim();
    setEditingId(null);
    if (!name || name === row.name) return;

    const collision = (data ?? []).find(
      (other) => other.id !== row.id && other.name.toLowerCase() === name.toLowerCase()
    );
    if (collision && !window.confirm(t('mergeConfirm', { source: row.name, target: collision.name }))) {
      return;
    }

    try {
      await update(
        resource,
        { id: row.id, data: { name }, previousData: row },
        { returnPromise: true, mutationMode: 'pessimistic' }
      );
      notify(collision ? t('merged') : t('renamed'), { type: 'info' });
      refetch();
    } catch {
      notify(t('error'), { type: 'error' });
    }
  };

  const handleDelete = async (row: CatalogRow) => {
    const message = row.usageCount > 0
      ? t('deleteConfirmUsed', { name: row.name, count: row.usageCount })
      : t('deleteConfirm', { name: row.name });
    if (!window.confirm(message)) return;

    deleteOne(
      resource,
      { id: row.id, previousData: row },
      {
        mutationMode: 'pessimistic',
        onSuccess: () => {
          notify(t('deleted'), { type: 'info' });
          refetch();
        },
        onError: () => notify(t('error'), { type: 'error' }),
      }
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Title title={t('title')} />
      <Card sx={{ border: 1, borderColor: 'divider' }}>
        <CardContent>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            {t('title')}
          </Typography>
          <Tabs
            value={resource}
            onChange={(_, value: string) => {
              setResource(value);
              setSearch('');
              setEditingId(null);
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2 }}
          >
            {CATALOGS.map((catalog) => (
              <Tab key={catalog} value={catalog} label={t(`types.${catalog}`)} />
            ))}
          </Tabs>

          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label={translate('common.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Box sx={{ flex: 1 }} />
            <TextField
              size="small"
              label={t('newName')}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
              }}
            />
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleCreate}>
              {translate('common.create')}
            </Button>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('columns.name')}</TableCell>
                <TableCell align="right">{t('columns.usage')}</TableCell>
                <TableCell align="right" sx={{ width: 120 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    {editingId === row.id ? (
                      <TextField
                        size="small"
                        value={editingName}
                        autoFocus
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRename(row);
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                      />
                    ) : (
                      row.name
                    )}
                  </TableCell>
                  <TableCell align="right">{row.usageCount}</TableCell>
                  <TableCell align="right">
                    {editingId === row.id ? (
                      <>
                        <IconButton size="small" onClick={() => handleRename(row)}>
                          <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => setEditingId(null)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <Tooltip title={t('rename')}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingId(row.id);
                              setEditingName(row.name);
                            }}
                          >
                            <RenameIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={translate('ra.action.delete')}>
                          <IconButton size="small" onClick={() => handleDelete(row)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {t('empty')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CatalogManager;
